// ClientCoaching.js - Version: v1 i14

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import BackButton from "./BackButton"; // Zurück-Button

const ClientCoaching = () => {
    const { tabellenname } = useParams(); // Tabellenname aus der URL holen
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const kontakt_id = localStorage.getItem("kontakt_id");

    const [tableData, setTableData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [coHeaders, setCoHeaders] = useState({});
    const [beschreibung, setBeschreibung] = useState("Lade...");
    const [anderbar, setAnderbar] = useState(false);
    const [editedData, setEditedData] = useState({}); // Speichert Änderungen an den Zellen

    useEffect(() => {
        if (!kontakt_id) {
            console.error("❌ Keine Kontakt-ID gefunden!");
            return;
        }

        if (!tabellenname) {
            console.log("📌 Lade Coaching-Tabellen für Kontakt-ID:", kontakt_id);
            axios
                .get(`/api/get_coaching_tabellen.php?kontakt_id=${kontakt_id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
                .then((response) => {
                    console.log("✅ API-Antwort für Coaching-Tabellen:", response.data);
                    if (response.data.success && Array.isArray(response.data.coaching_tabellen)) {
                        setTableData(response.data.coaching_tabellen);
                    } else {
                        console.error("❌ Fehler: Tabellen-Daten ungültig:", response.data);
                        setTableData([]);
                    }
                })
                .catch((error) => console.error("❌ Fehler beim Laden der Coaching-Tabellen:", error));
        } else {
            console.log("📌 Lade Daten für Tabelle:", tabellenname);

            axios
                .get(`/api/get_table_data.php?kontakt_id=${kontakt_id}&tabellenname=${tabellenname}`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
                .then((response) => {
                    console.log("✅ Empfangene Tabellendaten:", response.data);
                    
                    if (response.data.success && Array.isArray(response.data.daten)) {
                        setTableData(response.data.daten);
                        setColumns(response.data.daten.length > 0 ? Object.keys(response.data.daten[0]) : []);
                        setAnderbar(response.data.anderbar === "J");
                    } else {
                        console.error("❌ Fehlerhafte API-Antwort:", response.data);
                        setTableData([]);
                        setColumns([]);
                    }
                })
                .catch((error) => console.error("❌ Fehler beim Laden der Tabellendaten:", error));

            console.log("📌 Lade Co-Header für Tabelle:", tabellenname);

            axios
                .get(`/api/get_co_headers.php?kontakt_id=${kontakt_id}&tabellenname=${tabellenname}`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
                .then((response) => {
                    console.log("✅ Empfangene Co-Header:", response.data);
                    if (response.data.success) {
                        setCoHeaders(response.data.co_headers);
                        setBeschreibung(response.data.beschreibung || "⚠️ Beschreibung nicht verfügbar");
                    } else {
                        console.error("❌ Fehler beim Laden der Co-Header:", response.data);
                    }
                })
                .catch((error) => console.error("❌ Fehler beim Abrufen der Co-Header:", error));
        }
    }, [tabellenname, kontakt_id, token]);  // Hinzufügen der Abhängigkeiten: 'kontakt_id' und 'token'

    const handleInputChange = (rowIndex, column, value) => {
        const updatedData = { ...editedData };
        if (!updatedData[rowIndex]) updatedData[rowIndex] = {};
        updatedData[rowIndex][column] = value;
        setEditedData(updatedData);
    };

    const handleSaveChanges = () => {
        if (Object.keys(editedData).length === 0) {
            console.warn("⚠️ Keine Änderungen zum Speichern.");
            return;
        }

        const updatedRows = tableData.map((row, index) => {
            if (editedData[index]) {
                return { ...row, ...editedData[index] };
            }
            return row;
        });

        console.log("📤 Sende Änderungen an Server:", updatedRows);

        axios
            .post("/api/update_table_data.php", {
                kontakt_id,
                tabellenname,
                daten: updatedRows,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                console.log("✅ Antwort vom Server:", response.data);
                if (response.data.success) {
                    setEditedData({});
                    setTableData(updatedRows);
                } else {
                    console.error("❌ Fehler beim Speichern:", response.data);
                }
            })
            .catch((error) => console.error("❌ Netzwerkfehler beim Speichern:", error));
    };

    return (
        <div className="p-4">
            <BackButton to={tabellenname ? "/client-coaching" : "/client-dashboard"} /> {/* Dynamischer Zurück-Button */}

            <h2 className="text-xl font-bold mb-4">📋 Dein Coaching-Bereich</h2>

            {!tabellenname ? (
                tableData.length === 0 ? (
                    <p className="text-gray-500">⚠️ Keine freigegebenen Coaching-Tabellen gefunden.</p>
                ) : (
                    <div className="max-h-64 overflow-y-auto border rounded p-2">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b">
                                    <th className="p-2 text-left">Beschreibung</th>
                                    <th className="p-2 text-center">Aktion</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.map((table) => (
                                    <tr key={table.tabellenname} className="border-b">
                                        <td className="p-2">{table.beschreibung}</td>
                                        <td className="p-2 text-center">
                                            <button
                                                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
                                                onClick={() => navigate(`/client-coaching/${table.tabellenname}`)}
                                            >
                                                Öffnen
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : (
                <div>
                    <h3 className="text-lg font-semibold mb-2">{beschreibung}</h3>
                    {columns.length === 0 ? (
                        <p className="text-gray-500">⚠️ Diese Tabelle enthält noch keine Daten.</p>
                    ) : (
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b">
                                    {columns
                                        .filter((col) => coHeaders[col]) // Nur Co-Header-Spalten anzeigen
                                        .map((column) => (
                                            <th key={column} className="p-2 text-left">{coHeaders[column]}</th>
                                        ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.map((row, rowIndex) => (
                                    <tr key={rowIndex} className="border-b">
                                        {columns
                                            .filter((col) => coHeaders[col])
                                            .map((column) => (
                                                <td key={column} className="p-2">
                                                    {anderbar ? (
                                                        <input
                                                            type="text"
                                                            value={editedData[rowIndex]?.[column] ?? row[column]}
                                                            onChange={(e) => handleInputChange(rowIndex, column, e.target.value)}
                                                            className="border p-1 w-full"
                                                        />
                                                    ) : (
                                                        row[column]
                                                    )}
                                                </td>
                                            ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {anderbar && (
                        <button onClick={handleSaveChanges} className="mt-4 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-700">
                            💾 Änderungen speichern
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientCoaching;
